import { keyBy } from 'lodash';

import { FullVizTypes, VizTypes } from './types';
import { ChartTypes } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT } from '@mode/shared/util-js';

export const DATA_LIMIT = 20000;
export const PIVOT_LIMIT = 2000;
export const ROW_HEIGHT_ATTR = 'data-row-height';
export const COOKIE_TRIAL_HIDE = 'modeHideTrial';
export const COOKIE_DATASOURCE_HIDE = 'modeHideDatasourceBanner';
export const SHOW_NEW_SQL_EDITOR_SETTINGS = 'modeShowNewSqlEditorSettings';
export const HIGHLIGHT_LIMIT = 35000;
export const BROWSER_LIMIT = 15728640;
export const MAX_COPY_COMPAT = 600000; // For browsers that don't support the clipboard API
export const MAX_COPY_BYTES = 50000000; // Based on our browser performance testing

export interface FormatOption {
  name: string;
  id: string | number;
}

export interface QuadrantDefinition extends FormatOption {
  icon: string;
}

export interface ColumnDataType extends FormatOption {
  type: string;
}

export interface OptionWithDefault<D> extends FormatOption {
  defaultSettings: () => D;
}

export enum ConnectDatabaseElementId {
  NAV_ACCOUNT_MENU_CTA = 5,
  COMMUNITY_DATA_SOURCE_BANNER_CTA = 6,
  ORG_DATA_SOURCE_BANNER_CTA = 7,
}

export interface ColumnType<D> extends OptionWithDefault<D> {
  sample?: string;
}

export interface ChartOptionDefinition {
  id: string;
  name: string;
  subtype:
    | 'bar'
    | 'hBar'
    | 'stackedBar'
    | 'stackedBar100'
    | 'hStackedBar'
    | 'hStackedBar100'
    | 'line'
    | 'linePlusBar'
    | 'area'
    | 'totalArea'
    | 'expandedArea'
    | 'pie'
    | 'donut'
    | 'scatter'
    | 'bigNumber'
    | 'bigValue'
    | 'pivotTable'
    | 'table'
    | 'vegas';
  icon: string;
  iconTrunc: string;
  iconShort: string;
  thumbnail: string;
  pickerText: string;
  emptySvgKey: string;
  errorSvgKey: string;
}

export interface ChartOptionSection {
  name: string;
  definitions: ReadonlyArray<ChartOptionDefinition>;
}

export interface CurrencyType extends FormatOption {
  symbol?: '\u0024' | '\u00A5' | 'CHF' | '\u20AC' | '\u00A3';
  symbolPlacement?: 'before' | 'after';
  thousandSeparator?: ' ' | ',' | '.';
  thousandGroup?: number;
  decimalStyle?: ' ' | '.' | ',';
  decimalPlaces?: number;
}

export interface ColorScaleType {
  id: string;
  name: string;
  colors: Array<string>;
}

export const THOUSAND_DIGITS: Array<FormatOption> = [
  {
    name: 'three digits',
    id: 3,
  },
  {
    name: 'four digits',
    id: 4,
  },
];

export const DECIMAL_STYLES: Array<FormatOption> = [
  {
    name: 'comma',
    id: ',',
  },
  {
    name: 'decimal',
    id: '.',
  },
  {
    name: 'space',
    id: ' ',
  },
  {
    name: 'none',
    id: '',
  },
];

export const DATE_FORMATS: Array<FormatOption> = [
  {
    name: 'YYYY-MM-DD HH:MM:SS',
    id: 'YYYY-MM-DD HH:mm:ss',
  },
  {
    name: 'YYYY-MM-DD',
    id: 'YYYY-MM-DD',
  },
  {
    name: 'Mon YYYY',
    id: 'MMM YYYY',
  },
  {
    name: 'Day Mon YYYY',
    id: 'D MMM YYYY',
  },
  {
    name: 'Mon Day YYYY',
    id: 'MMM D YYYY',
  },
  {
    name: 'None',
    id: 'none',
  },
];

export const COLUMN_DATA_TYPES: Array<ColumnDataType> = [
  {
    name: 'Number',
    id: 'number',
    type: 'number',
  },
  {
    name: 'Integer',
    id: 'integer',
    type: 'number',
  },
  {
    name: 'Bigint',
    id: 'bigint',
    type: 'number',
  },
  {
    name: 'Float',
    id: 'float',
    type: 'number',
  },
  {
    name: 'Date',
    id: 'date',
    type: 'date',
  },
  {
    name: 'Datetime',
    id: 'datetime',
    type: 'date',
  },
  {
    name: 'String',
    id: 'string',
    type: 'string',
  },
];

export const COLUMN_NUMBER_DATA_TYPE_IDS: Array<string | number> = COLUMN_DATA_TYPES.filter(
  (t: ColumnDataType) => t.type === 'number'
).map((t: ColumnDataType) => t.id);

export const COLUMN_DATE_DATA_TYPE_IDS: Array<string | number> = COLUMN_DATA_TYPES.filter(
  (t: ColumnDataType) => t.type === 'date'
).map((t: ColumnDataType) => t.id);

export const COLUMN_STRING_DATA_TYPE_IDS: Array<string | number> = COLUMN_DATA_TYPES.filter(
  (t: ColumnDataType) => t.type === 'string'
).map((t: ColumnDataType) => t.id);

export const COLUMN_TYPES: Array<ColumnType<any>> = [
  {
    name: 'Automatic',
    id: 'automatic',
    defaultSettings: function () {
      return {};
    },
  },
  {
    name: 'Plain Text',
    id: 'plain-text',
    defaultSettings: function () {
      return {};
    },
  },
  {
    name: 'Date',
    id: 'date',
    defaultSettings: function () {
      return { preset: 'YYYY-MM-DD HH:mm:ss' };
    },
  },
  {
    name: 'Number',
    id: 'number',
    defaultSettings: function () {
      return {
        decimalStyle: '.',
        decimalPlaces: 2,
        thousandSeparator: ',',
        thousandGroup: 3,
      };
    },
    sample: '1,000.16',
  },
  {
    name: 'Percentage',
    id: 'percentage',
    defaultSettings: function () {
      return {
        decimalStyle: '.',
        decimalPlaces: 2,
      };
    },
    sample: '10.16%',
  },
  {
    name: 'Financial',
    id: 'financial',
    defaultSettings: function () {
      return { type: ChartTypes.CurrencyPreset.USD };
    },
    sample: '(1,000.16)',
  },
  {
    name: 'Currency',
    id: 'currency',
    defaultSettings: function () {
      return { type: ChartTypes.CurrencyPreset.USD };
    },
    sample: '$1,000.16',
  },
];

export const CURRENCY_TYPES: Array<CurrencyType> = [
  {
    name: 'US Dollar',
    id: ChartTypes.CurrencyPreset.USD,
    symbol: '\u0024',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Euro',
    id: ChartTypes.CurrencyPreset.EUR,
    symbol: '\u20AC',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Japanese Yen',
    id: ChartTypes.CurrencyPreset.JPY,
    symbol: '\u00A5',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 4,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'British Pound Sterling',
    id: ChartTypes.CurrencyPreset.GBP,
    symbol: '\u00A3',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Australian Dollar',
    id: ChartTypes.CurrencyPreset.AUD,
    symbol: '\u0024',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Swiss Franc',
    id: ChartTypes.CurrencyPreset.CHF,
    symbol: 'CHF',
    symbolPlacement: 'before',
    thousandSeparator: ' ',
    thousandGroup: 3,
    decimalStyle: ',',
    decimalPlaces: 2,
  },
  {
    name: 'Canadian Dollar',
    id: ChartTypes.CurrencyPreset.CAD,
    symbol: '\u0024',
    symbolPlacement: 'before',
    thousandSeparator: ' ',
    thousandGroup: 3,
    decimalStyle: ',',
    decimalPlaces: 2,
  },
  {
    name: 'Mexican Peso',
    id: ChartTypes.CurrencyPreset.MXN,
    symbol: '\u0024',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Chinese Yuan',
    id: ChartTypes.CurrencyPreset.CNY,
    symbol: '\u00A5',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 4,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'New Zealand Dollar',
    id: ChartTypes.CurrencyPreset.NZD,
    symbol: '\u0024',
    symbolPlacement: 'before',
    thousandSeparator: ',',
    thousandGroup: 3,
    decimalStyle: '.',
    decimalPlaces: 2,
  },
  {
    name: 'Custom...',
    id: ChartTypes.CurrencyPreset.Custom,
  },
];

export const NUMBER_AGGREGATOR_TYPES: Array<FormatOption> = [
  {
    id: 'last_value',
    name: 'Last Value',
  },
  {
    id: 'min_value',
    name: 'Min Value',
  },
  {
    id: 'max_value',
    name: 'Max Value',
  },
  {
    id: 'average',
    name: 'Average',
  },
  {
    id: 'count',
    name: 'Count',
  },
  {
    id: 'count_distinct',
    name: 'Count (distinct)',
  },
  {
    id: 'median',
    name: 'Median',
  },
  {
    id: 'sum',
    name: 'Sum',
  },
];

export const TREND_TYPES: Array<FormatOption> = [
  {
    id: 'change_rate',
    name: 'Rate of change',
  },
  {
    id: 'total_change',
    name: 'Total change',
  },
  {
    id: 'none',
    name: 'None',
  },
];

export const RELATIVE_RANGE_POINT_TYPES: Array<FormatOption> = [
  {
    id: 'previous',
    name: 'Previous value',
  },
  {
    id: 'first',
    name: 'First value',
  },
  {
    id: 'custom',
    name: 'Custom...',
  },
];

export const ORDER_TYPES: Array<FormatOption> = [
  {
    id: 'asc',
    name: 'Ascending',
  },
  {
    id: 'desc',
    name: 'Descending',
  },
];

export const TREND_COLOR_SCALES: Array<ColorScaleType> = [
  {
    id: 'ggr',
    name: 'Green/Gray/Red',
    colors: ['#25b261', '#c3cbcb', '#e95968'],
  },
  {
    id: 'rgg',
    name: 'Red/Gray/Green',
    colors: ['#e95968', '#c3cbcb', '#25b261'],
  },
];

export const TREND_PRECISION_TYPES: Array<FormatOption> = [
  {
    id: 'auto',
    name: 'Auto',
  },
  {
    id: 0,
    name: '1',
  },
  {
    id: 1,
    name: '0.1',
  },
  {
    id: 2,
    name: '0.01',
  },
  {
    id: 3,
    name: '0.001',
  },
];

export const TREND_FORMAT_TYPES: Array<FormatOption> = [
  {
    id: 'auto',
    name: 'Auto',
  },
  {
    id: 'percent',
    name: 'Percent',
  },
  {
    id: 'number',
    name: 'Number',
  },
];

export const CHART_OPTION_TYPES: ReadonlyArray<ChartOptionDefinition> = [
  {
    id: 'area',
    name: 'Area Chart',
    subtype: 'area',
    pickerText: 'Area',
    icon: 'icon-chart-area-gray',
    iconTrunc: 'chart-area-gray',
    iconShort: 'area',
    thumbnail: 'icon-chart-thumbnail-area',
    emptySvgKey: 'empty-area',
    errorSvgKey: 'error-area',
  },
  {
    id: 'totalArea',
    name: '100% Area Chart',
    subtype: 'totalArea',
    pickerText: '100% Area',
    icon: 'icon-chart-area100-gray',
    iconTrunc: 'chart-area100-gray',
    iconShort: 'area100',
    thumbnail: 'icon-chart-thumbnail-area-100',
    emptySvgKey: 'empty-total-area',
    errorSvgKey: 'error-total-area',
  },
  {
    id: 'bar',
    name: 'Grouped Bar Chart',
    subtype: 'bar',
    pickerText: 'Grouped Bar',
    icon: 'icon-chart-grouped-bar-gray',
    iconTrunc: 'chart-grouped-bar-gray',
    iconShort: 'bar',
    thumbnail: 'icon-chart-thumbnail-groupedbar',
    emptySvgKey: 'empty-bar',
    errorSvgKey: 'error-grouped-bar',
  },
  {
    id: 'hBar',
    name: 'Bar Chart',
    subtype: 'bar',
    pickerText: 'Grouped Bar',
    icon: 'icon-chart-h-grouped-bar-gray',
    iconTrunc: 'chart-h-grouped-bar-gray',
    iconShort: 'bar',
    thumbnail: 'icon-chart-thumbnail-h-groupedbar',
    emptySvgKey: 'empty-h-bar',
    errorSvgKey: 'error-h-bar',
  },
  {
    id: 'stackedBar',
    name: 'Bar Chart',
    subtype: 'stackedBar',
    pickerText: 'Bar',
    icon: 'icon-chart-bar-gray',
    iconTrunc: 'chart-bar-gray',
    iconShort: 'stackedbar',
    thumbnail: 'icon-chart-thumbnail-bar',
    emptySvgKey: 'empty-stacked-bar',
    errorSvgKey: 'error-stacked-bar',
  },
  {
    id: 'stackedBar100',
    name: '100% Bar Chart',
    pickerText: '100% Bar',
    subtype: 'stackedBar100',
    icon: 'icon-chart-bar-100-gray',
    iconTrunc: 'chart-bar-100-gray',
    iconShort: 'stackedbar100',
    thumbnail: 'icon-chart-thumbnail-bar-100',
    emptySvgKey: 'empty-stacked-bar-100',
    errorSvgKey: 'error-stacked-bar-100',
  },
  {
    id: 'hStackedBar',
    name: 'Bar Chart',
    subtype: 'hStackedBar',
    pickerText: 'Bar',
    icon: 'icon-chart-h-bar-gray',
    iconTrunc: 'chart-h-bar-gray',
    iconShort: 'hStackedbar',
    thumbnail: 'icon-chart-thumbnail-h-bar',
    emptySvgKey: 'empty-h-stacked-bar',
    errorSvgKey: 'error-h-bar',
  },
  {
    id: 'hStackedBar100',
    name: '100% Bar Chart',
    subtype: 'hStackedBar100',
    pickerText: '100% Bar',
    icon: 'icon-chart-h-bar-100-gray',
    iconTrunc: 'chart-h-bar-100-gray',
    iconShort: 'hStackedbar100',
    thumbnail: 'icon-chart-thumbnail-h-bar-100',
    emptySvgKey: 'empty-h-stacked-bar-100',
    errorSvgKey: 'error-h-stacked-bar-100',
  },
  {
    id: 'bigNumber',
    name: 'Big Number',
    subtype: 'bigNumber',
    pickerText: 'Big Number',
    icon: 'icon-chart-bignumber-gray',
    iconTrunc: 'chart-bignumber-gray',
    iconShort: 'bignumber',
    thumbnail: 'icon-chart-thumbnail-bignumber', // Doesn't exist
    emptySvgKey: 'empty-big-value',
    errorSvgKey: 'error-big-value',
  },
  {
    id: 'bigValue',
    name: 'Big Value',
    subtype: 'bigValue',
    pickerText: 'Big Value',
    icon: 'icon-chart-bigvalue-gray',
    iconTrunc: 'chart-bigvalue-gray',
    iconShort: 'bigvalue',
    thumbnail: 'icon-chart-thumbnail-bigvalue',
    emptySvgKey: 'empty-big-value',
    errorSvgKey: 'error-big-value',
  },
  {
    id: 'line',
    name: 'Line Chart',
    subtype: 'line',
    pickerText: 'Line',
    icon: 'icon-chart-line-gray',
    iconTrunc: 'chart-line-gray',
    iconShort: 'line',
    thumbnail: 'icon-chart-thumbnail-line',
    emptySvgKey: 'empty-line',
    errorSvgKey: 'error-line',
  },
  {
    id: 'linePlusBar',
    name: 'Line + Bar Chart',
    subtype: 'linePlusBar',
    pickerText: 'Line + Bar',
    icon: 'icon-chart-linebar-gray',
    iconTrunc: 'chart-linebar-gray',
    iconShort: 'linebar',
    thumbnail: 'icon-chart-thumbnail-linebar',
    emptySvgKey: 'empty-line-plus-bar',
    errorSvgKey: 'error-line-plus-bar',
  },
  {
    id: 'pie',
    name: 'Pie Chart',
    subtype: 'pie',
    pickerText: 'Pie',
    icon: 'icon-chart-pie-gray',
    iconTrunc: 'chart-pie-gray',
    iconShort: 'pie',
    thumbnail: 'icon-chart-thumbnail-pie',
    emptySvgKey: 'empty-pie',
    errorSvgKey: 'error-pie',
  },
  {
    id: 'donut',
    name: 'Donut Chart',
    subtype: 'donut',
    pickerText: 'Donut',
    icon: 'icon-chart-donut-gray',
    iconTrunc: 'chart-donut-gray',
    iconShort: 'donut',
    thumbnail: 'icon-chart-thumbnail-donut',
    emptySvgKey: 'empty-donut',
    errorSvgKey: 'error-donut',
  },
  {
    id: 'pivotTable',
    name: 'Pivot Table',
    subtype: 'pivotTable',
    pickerText: 'Pivot Table',
    icon: 'icon-chart-pivot-gray',
    iconTrunc: 'chart-pivot-gray',
    iconShort: 'pivot',
    thumbnail: 'icon-chart-thumbnail-pivot-table', // Doesn't exist
    emptySvgKey: 'empty-table',
    errorSvgKey: 'error-table',
  },
  {
    id: 'scatter',
    name: 'Scatter Plot',
    subtype: 'scatter',
    pickerText: 'Scatter',
    icon: 'icon-chart-scatter-gray',
    iconTrunc: 'chart-scatter-gray',
    iconShort: 'scatter',
    thumbnail: 'icon-chart-thumbnail-scatter',
    emptySvgKey: 'empty-scatter',
    errorSvgKey: 'error-scatter',
  },
  {
    id: 'vegas',
    name: 'Visual Explorer',
    subtype: 'vegas',
    pickerText: 'Visual Explorer',
    icon: 'icon-chart-explorer',
    iconTrunc: 'chart-explorer',
    iconShort: 'explorer',
    thumbnail: 'illo-chart-thumb-explorer',
    emptySvgKey: 'empty-scatter',
    errorSvgKey: 'error-viz-explorer',
  },
];

export const V2_CHART_OPTION_TYPES: ReadonlyArray<ChartOptionDefinition> = [
  ...CHART_OPTION_TYPES,
  {
    id: 'table',
    name: 'Table',
    subtype: 'table',
    pickerText: 'Table',
    icon: 'icon-chart-table-gray',
    iconTrunc: 'chart-table-gray',
    iconShort: 'table',
    thumbnail: 'icon-chart-thumbnail-table',
    emptySvgKey: 'empty-table',
    errorSvgKey: 'error-table',
  },
  {
    id: 'vegas',
    name: 'Visual Explorer',
    subtype: 'vegas',
    pickerText: 'Visual Explorer',
    icon: 'icon-chart-explorer',
    iconTrunc: 'chart-explorer',
    iconShort: 'explorer',
    thumbnail: 'illo-chart-thumb-explorer',
    emptySvgKey: 'empty-scatter',
    errorSvgKey: 'error-viz-explorer',
  },
];

export const CHART_OPTION_TYPES_WITH_KEY = keyBy(CHART_OPTION_TYPES, 'id');
export const V2_CHART_OPTION_TYPES_WITH_KEY = keyBy(V2_CHART_OPTION_TYPES, 'id');
export const VIZ_PICKER_OPTIONS: ReadonlyArray<ChartOptionDefinition> = [
  V2_CHART_OPTION_TYPES_WITH_KEY['table'],
  CHART_OPTION_TYPES_WITH_KEY['bigValue'],
  CHART_OPTION_TYPES_WITH_KEY['bar'],
  CHART_OPTION_TYPES_WITH_KEY['stackedBar'],
  CHART_OPTION_TYPES_WITH_KEY['stackedBar100'],
  CHART_OPTION_TYPES_WITH_KEY['hBar'],
  CHART_OPTION_TYPES_WITH_KEY['hStackedBar'],
  CHART_OPTION_TYPES_WITH_KEY['hStackedBar100'],
  CHART_OPTION_TYPES_WITH_KEY['line'],
  CHART_OPTION_TYPES_WITH_KEY['linePlusBar'],
  CHART_OPTION_TYPES_WITH_KEY['area'],
  CHART_OPTION_TYPES_WITH_KEY['totalArea'],
  CHART_OPTION_TYPES_WITH_KEY['scatter'],
  CHART_OPTION_TYPES_WITH_KEY['pie'],
  CHART_OPTION_TYPES_WITH_KEY['donut'],
  // V2_CHART_OPTION_TYPES_WITH_KEY['vegas'], // Uncomment when removing FeatureFlag.VegasEncoding
];

export const CHART_TYPES = CHART_OPTION_TYPES.map((o) => o.id);

export const EMPTY_LEGACY_CHART: ChartTypes.ChartView = {
  bar: {},
  bigNumber: {},
  line: {},
  linePlusBar: {},
  scatter: {},
  pie: {},
  area: {},
  pivotTable: {},
  selectedChart: 'line',
  chartIcon: null,
  chartTitle: null,
  chartDescription: null,
};

export const EMPTY_VEGAS_CHART = {
  chartType: 'line' as ChartTypes.ChartType,
  format: DEFAULT_CHART_FORMAT,
};

export const FLAMINGO_UNSUPPORTED_CHARTS = ['table', 'pivotTable'];

export const PIVOT_QUADRANTS: Array<QuadrantDefinition> = [
  {
    id: 'filters',
    name: 'Filters',
    icon: 'icon-pivot-filter',
  },
  {
    id: 'columns',
    name: 'Columns',
    icon: 'icon-columns',
  },
  {
    id: 'rows',
    name: 'Rows',
    icon: 'icon-rows',
  },
  {
    id: 'values',
    name: 'Values',
    icon: 'icon-pivot-value',
  },
];

export const DEFAULT_DIVERGENT_PALETTE = ['#25B261', '#B1B2BA', '#E95968'];
export const MAX_REPORT_GRID_COLUMNS = 12;
export const MIN_REPORT_GRID_COLUMNS = 2; // Minimum column width for a visualization

export const VIZ_SELECTORS = Object.keys(VizTypes)
  .map((k) => 'mode-' + VizTypes[k])
  .join(',');
export const FULL_VIZ_TAGS = Object.keys(FullVizTypes).map((k) => ('mode-' + FullVizTypes[k]).toUpperCase());
export const TEXT_BLOCK_MARGINS_CLASS = 'text-margins';

export const DefaultThemes: Array<string> = ['Mode Dark', 'Mode Grey', 'Mode Light', 'Mode Subway'];

export const TABLE_ENCODING_BUILDER_PILL_HEIGHT = 22; // Height of Pill in Table Encoding Builder
export const TABLE_ENCODING_BUILDER_PILL_MARGIN = 4; // Margin between two Pills in Table Encoding Builder;
